import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock3, Upload, X } from "lucide-react";
import { toast } from "react-toastify";
import Navbar from "../SharedComponents/Navbar";
import Footer from "../SharedComponents/Footer";
import useUserStore from "../store/userStore";
import {
  createPlaceContributionDraft,
  deletePlaceContributionDraft,
  fetchContributionCategories,
  fetchContributionCities,
  fetchContributionTags,
  fetchPlaceContributionSubmissions,
  getPlaceContributionSubmission,
  PlaceContributionApiError,
  PlaceContributionDraft,
  PlaceContributionSubmission,
  OpeningScheduleDay,
  submitPlaceContributionDraft,
  UploadedContributionImage,
  uploadPlaceContributionImage,
  updatePlaceContributionDraft,
} from "../services/placeContributionsService";

type DraftFields = {
  name: string;
  city: string;
  category: string;
  address: string;
  description: string;
  phone: string;
  websiteUrl: string;
  instagramUrl: string;
  googleMapsUrl: string;
};

const initialFields: DraftFields = {
  name: "",
  city: "",
  category: "",
  address: "",
  description: "",
  phone: "",
  websiteUrl: "",
  instagramUrl: "",
  googleMapsUrl: "",
};

type ScheduleInput = {
  dayOfWeek: number;
  label: string;
  enabled: boolean;
  opensAt: string;
  closesAt: string;
};

type SelectedImage = {
  id: string;
  file?: File;
  previewUrl: string;
  objectUrl?: string;
  uploadedImage?: UploadedContributionImage;
  errorMessage?: string;
  status: "ready" | "uploading" | "uploaded" | "error";
};

const MAX_CONTRIBUTION_IMAGE_SIZE = 20 * 1024 * 1024;
const SUPPORTED_CONTRIBUTION_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
]);

const getFirstFieldError = (value: string[] | string | undefined) => (
  Array.isArray(value) ? value.find(Boolean) : value
);

// The numeric values follow the backend's Python weekday convention: Monday=0.
const initialSchedule: ScheduleInput[] = [
  { dayOfWeek: 5, label: "شنبه", enabled: false, opensAt: "09:00", closesAt: "23:00" },
  { dayOfWeek: 6, label: "یکشنبه", enabled: false, opensAt: "09:00", closesAt: "23:00" },
  { dayOfWeek: 0, label: "دوشنبه", enabled: false, opensAt: "09:00", closesAt: "23:00" },
  { dayOfWeek: 1, label: "سه‌شنبه", enabled: false, opensAt: "09:00", closesAt: "23:00" },
  { dayOfWeek: 2, label: "چهارشنبه", enabled: false, opensAt: "09:00", closesAt: "23:00" },
  { dayOfWeek: 3, label: "پنج‌شنبه", enabled: false, opensAt: "09:00", closesAt: "23:00" },
  { dayOfWeek: 4, label: "جمعه", enabled: false, opensAt: "09:00", closesAt: "23:00" },
];

const createIdempotencyKey = () => (
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
);

const getIdempotencyStorageKey = (userId?: string) => (
  `lootka-place-contribution-idempotency:${userId ?? "anonymous"}`
);

const getOrCreateIdempotencyKey = (userId?: string) => {
  const storageKey = getIdempotencyStorageKey(userId);
  try {
    const storedKey = window.sessionStorage.getItem(storageKey);
    if (storedKey) return storedKey;

    const nextKey = createIdempotencyKey();
    window.sessionStorage.setItem(storageKey, nextKey);
    return nextKey;
  } catch {
    return createIdempotencyKey();
  }
};

const clearIdempotencyKey = (userId?: string) => {
  try {
    window.sessionStorage.removeItem(getIdempotencyStorageKey(userId));
  } catch {
    // Storage can be unavailable in private browsing; submission still succeeds.
  }
};

const isHttpUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const isGoogleMapsUrl = (value: string) => {
  try {
    const hostname = new URL(value).hostname.toLowerCase();
    return hostname === "maps.app.goo.gl"
      || hostname === "maps.google.com"
      || hostname.endsWith(".google.com");
  } catch {
    return false;
  }
};

const isInstagramUrl = (value: string) => {
  try {
    const hostname = new URL(value).hostname.toLowerCase();
    return hostname === "instagram.com" || hostname === "www.instagram.com";
  } catch {
    return false;
  }
};

const getOptionId = (value: number | { id: number } | undefined) => (
  typeof value === "number" ? value : value?.id
);

const formatDraftDate = (value?: string) => {
  if (!value) return "زمان نامشخص";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "زمان نامشخص";
  return new Intl.DateTimeFormat("fa-IR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const PlaceContributionPage: React.FC = () => {
  const user = useUserStore((state) => state.user);
  const queryClient = useQueryClient();
  const [fields, setFields] = useState<DraftFields>(initialFields);
  const [idempotencyKey, setIdempotencyKey] = useState(() => getOrCreateIdempotencyKey(user?.userId));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState(0);
  const [dailyLimitReached, setDailyLimitReached] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[] | string>>({});
  const [fieldCodes, setFieldCodes] = useState<Record<string, string[]>>({});
  const [submission, setSubmission] = useState<PlaceContributionSubmission | null>(null);
  const [isRestoredDraft, setIsRestoredDraft] = useState(false);
  const [editingSubmissionId, setEditingSubmissionId] = useState<string | null>(null);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [showAllTags, setShowAllTags] = useState(false);
  const [schedule, setSchedule] = useState<ScheduleInput[]>(initialSchedule);
  const [isEveryDay, setIsEveryDay] = useState(false);
  const [everyDayHours, setEveryDayHours] = useState({ opensAt: "09:00", closesAt: "23:00" });
  const [selectedImages, setSelectedImages] = useState<SelectedImage[]>([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isCancellingDraft, setIsCancellingDraft] = useState(false);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const selectedImagesRef = useRef<SelectedImage[]>([]);
  const formRef = useRef<HTMLFormElement>(null);
  const submitLockRef = useRef(false);
  const originalDraftPayloadRef = useRef<PlaceContributionDraft | null>(null);

  const citiesQuery = useQuery({
    queryKey: ["place-contribution-cities"],
    queryFn: ({ signal }) => fetchContributionCities(signal),
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });
  const categoriesQuery = useQuery({
    queryKey: ["place-contribution-categories"],
    queryFn: ({ signal }) => fetchContributionCategories(signal),
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });
  const tagsQuery = useQuery({
    queryKey: ["place-contribution-tags"],
    queryFn: ({ signal }) => fetchContributionTags(signal),
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });
  const submissionsQuery = useQuery({
    queryKey: ["place-contribution-submissions", user?.userId],
    queryFn: ({ signal }) => fetchPlaceContributionSubmissions(signal),
    enabled: Boolean(user),
    staleTime: 30 * 1000,
    retry: false,
  });

  const taxonomyError = citiesQuery.error || categoriesQuery.error || tagsQuery.error;
  const isTaxonomyLoading = citiesQuery.isLoading || categoriesQuery.isLoading || tagsQuery.isLoading;
  const isTaxonomyReady = Boolean(citiesQuery.data && categoriesQuery.data && tagsQuery.data);

  useEffect(() => {
    selectedImagesRef.current = selectedImages;
  }, [selectedImages]);

  useEffect(() => () => {
    selectedImagesRef.current.forEach((image) => {
      if (image.objectUrl) URL.revokeObjectURL(image.objectUrl);
    });
  }, []);

  useEffect(() => {
    if (retryAfterSeconds <= 0) return undefined;
    const timer = window.setInterval(() => {
      setRetryAfterSeconds((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [retryAfterSeconds]);

  useEffect(() => {
    const error = submissionsQuery.error;
    if (error instanceof PlaceContributionApiError && error.code === "request_rate_limited") {
      setRetryAfterSeconds(error.retryAfterSeconds ?? 60);
    }
    if (error instanceof Error) {
      toast.error(error.message, { className: "toast", toastId: "place-contribution-drafts-error" });
    }
  }, [submissionsQuery.error]);

  useEffect(() => {
    if (formError) {
      toast.error(formError, { className: "toast", toastId: "place-contribution-form-error" });
    }
  }, [formError]);

  useEffect(() => {
    if (imageUploadError) {
      toast.error(imageUploadError, { className: "toast", toastId: "place-contribution-image-error" });
    }
  }, [imageUploadError]);

  useEffect(() => {
    if (finalizeError) {
      toast.error(finalizeError, { className: "toast", toastId: "place-contribution-finalize-error" });
    }
  }, [finalizeError]);

  useEffect(() => {
    if (taxonomyError instanceof Error) {
      toast.error("دریافت شهرها، دسته‌بندی‌ها یا برچسب‌ها ممکن نشد.", {
        className: "toast",
        toastId: "place-contribution-taxonomy-error",
      });
    }
  }, [taxonomyError]);

  const fieldMessages = (field: string): string[] => {
    const message = fieldErrors[field]
      ?? (field === "tags" ? fieldErrors["برچسب‌ها"] : undefined)
      ?? (field === "google_maps_url" ? fieldErrors.location : undefined);
    if (!message) return [];
    return Array.isArray(message) ? message : [message];
  };

  const focusFirstInvalidField = (errors: Record<string, string[] | string>) => {
    const firstField = Object.keys(errors)[0];
    if (!firstField) return;
    const normalizedField = firstField === "برچسب‌ها"
      ? "tags"
      : firstField === "location"
        ? "google_maps_url"
        : firstField;
    window.requestAnimationFrame(() => {
      const target = formRef.current?.querySelector<HTMLElement>(`[data-field="${normalizedField}"]`);
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      const control = target?.matches("input, select, textarea, button")
        ? target
        : target?.querySelector<HTMLElement>("input, select, textarea, button");
      control?.focus({ preventScroll: true });
    });
  };

  const handleOpenDraft = async (draft: PlaceContributionSubmission) => {
    setFormError(null);
    setIsLoadingDraft(true);
    try {
      const detail = await getPlaceContributionSubmission(draft.submission_id);
      const cityId = getOptionId(detail.city);
      const categoryId = getOptionId(detail.categories?.[0]);
      const tagIds = detail.tags?.map(getOptionId).filter((id): id is number => typeof id === "number") ?? [];

      setFields({
        name: detail.name ?? "",
        city: cityId ? String(cityId) : "",
        category: categoryId ? String(categoryId) : "",
        address: detail.address ?? "",
        description: detail.description ?? "",
        phone: detail.phone ?? "",
        websiteUrl: detail.website_url ?? "",
        instagramUrl: detail.instagram_url ?? "",
        googleMapsUrl: detail.google_maps_url ?? "",
      });
      setSelectedTags(tagIds);
      originalDraftPayloadRef.current = {
        name: detail.name ?? "",
        city: cityId ?? 0,
        categories: categoryId ? [categoryId] : [],
        tags: tagIds,
        address: detail.address || undefined,
        description: detail.description || undefined,
        phone: detail.phone || undefined,
        website_url: detail.website_url || undefined,
        instagram_url: detail.instagram_url || undefined,
        google_maps_url: detail.google_maps_url || undefined,
        opening_schedule: detail.opening_schedule?.length ? detail.opening_schedule : undefined,
        timezone: "Asia/Tehran",
      };

      if (detail.opening_schedule?.length) {
        setSchedule(initialSchedule.map((day) => {
          const savedDay = detail.opening_schedule?.find((item) => item.day_of_week === day.dayOfWeek);
          const interval = savedDay?.intervals?.[0];
          return savedDay && interval
            ? { ...day, enabled: true, opensAt: interval.opens_at, closesAt: interval.closes_at }
            : { ...day };
        }));
      } else {
        setSchedule(initialSchedule);
      }

      setSelectedImages((detail.images ?? draft.images ?? []).map((image) => ({
        id: image.id,
        previewUrl: image.url,
        uploadedImage: image,
        status: "uploaded" as const,
      })));
      setFieldErrors({});
      setFieldCodes({});
      setIsRestoredDraft(true);
      setEditingSubmissionId(detail.submission_id ?? draft.submission_id);
      setShowNewForm(false);
      if (detail.warnings?.length) {
        toast.warn(detail.warnings.join(" — "), {
          className: "toast",
          toastId: `place-contribution-draft-warning-${draft.submission_id}`,
        });
      } else {
        toast.info("پیش‌نویس برای تکمیل یا ویرایش باز شد.", {
          className: "toast",
          toastId: "place-contribution-draft-opened",
        });
      }
    } catch (error) {
      setFormError(error instanceof PlaceContributionApiError
        ? error.message
        : "بازکردن پیش‌نویس ممکن نشد. لطفاً دوباره تلاش کنید.");
    } finally {
      setIsLoadingDraft(false);
    }
  };

  const updateField = (field: keyof DraftFields, value: string, apiField: string = field) => {
    setFields((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => {
      const remaining = { ...current };
      delete remaining[field];
      delete remaining[apiField];
      return remaining;
    });
    setFieldCodes((current) => {
      const remaining = { ...current };
      delete remaining[field];
      delete remaining[apiField];
      return remaining;
    });
  };

  const updateScheduleDay = (dayOfWeek: number, update: Partial<ScheduleInput>) => {
    setSchedule((current) => current.map((day) => (
      day.dayOfWeek === dayOfWeek ? { ...day, ...update } : day
    )));
  };

  const toggleEveryDay = (enabled: boolean) => {
    setIsEveryDay(enabled);
    setSchedule((current) => current.map((day) => ({
      ...day,
      enabled,
      opensAt: everyDayHours.opensAt,
      closesAt: everyDayHours.closesAt,
    })));
  };

  const updateEveryDayHours = (field: "opensAt" | "closesAt", value: string) => {
    setEveryDayHours((current) => ({ ...current, [field]: value }));
    setSchedule((current) => current.map((day) => ({ ...day, [field]: value, enabled: true })));
  };

  const toggleTag = (tagId: number) => {
    setSelectedTags((current) => (
      current.includes(tagId) ? current.filter((id) => id !== tagId) : [...current, tagId]
    ));
    setFieldErrors((current) => {
      const remaining = { ...current };
      delete remaining.tags;
      delete remaining["برچسب‌ها"];
      return remaining;
    });
    setFieldCodes((current) => {
      const remaining = { ...current };
      delete remaining.tags;
      return remaining;
    });
  };

  const addImages = (files: FileList | null) => {
    if (!files) return;

    const availableSlots = Math.max(0, 5 - selectedImages.length);
    const requestedFiles = Array.from(files);
    const validFiles = requestedFiles.filter((file) => (
      SUPPORTED_CONTRIBUTION_IMAGE_TYPES.has(file.type)
      && file.size <= MAX_CONTRIBUTION_IMAGE_SIZE
    ));
    const supportedFiles = validFiles.slice(0, availableSlots);

    if (supportedFiles.length === 0) {
      const hasOversizedFile = requestedFiles.some((file) => file.size > MAX_CONTRIBUTION_IMAGE_SIZE);
      setImageUploadError(hasOversizedFile
        ? "حجم هر تصویر باید حداکثر ۲۰ مگابایت باشد."
        : "فقط تصویر با فرمت JPEG، PNG، GIF یا WebP قابل بارگذاری است.");
      return;
    }

    const rejectedCount = requestedFiles.length - supportedFiles.length;
    setImageUploadError(rejectedCount > 0
      ? "برخی فایل‌ها به‌دلیل فرمت نامعتبر، حجم بیشتر از ۲۰ مگابایت یا محدودیت پنج تصویر اضافه نشدند."
      : null);
    setSelectedImages((current) => [
      ...current,
      ...supportedFiles.map((file) => {
        const objectUrl = URL.createObjectURL(file);
        return {
          id: `${file.name}-${file.lastModified}-${Math.random().toString(16).slice(2)}`,
          file,
          previewUrl: objectUrl,
          objectUrl,
          status: "ready" as const,
        };
      }),
    ]);
  };

  const removeImage = (imageId: string) => {
    setSelectedImages((current) => {
      const removedImage = current.find((image) => image.id === imageId);
      if (removedImage?.objectUrl) URL.revokeObjectURL(removedImage.objectUrl);
      return current.filter((image) => image.id !== imageId);
    });
  };

  const setImageStatus = (imageId: string, status: SelectedImage["status"], errorMessage?: string) => {
    setSelectedImages((current) => current.map((image) => (
      image.id === imageId ? { ...image, status, errorMessage } : image
    )));
  };

  const setUploadedImage = (imageId: string, uploadedImage: UploadedContributionImage) => {
    setSelectedImages((current) => current.map((image) => {
      if (image.id !== imageId) return image;
      if (image.objectUrl) URL.revokeObjectURL(image.objectUrl);
      return {
        ...image,
        previewUrl: uploadedImage.url,
        objectUrl: undefined,
        uploadedImage,
        errorMessage: undefined,
        status: "uploaded",
      };
    }));
  };

  const uploadSelectedImages = async (submissionId: string) => {
    const imagesToUpload = selectedImages.filter((image) => (
      (image.status === "ready" || image.status === "error") && image.file
    ));
    if (imagesToUpload.length === 0) return selectedImages.some((image) => image.status === "uploaded");

    setIsUploadingImages(true);
    setImageUploadError(null);
    let failedUploads = 0;
    let firstUploadError: string | null = null;

    for (const image of imagesToUpload) {
      setImageStatus(image.id, "uploading");
      try {
        const uploadedImage = await uploadPlaceContributionImage(submissionId, image.file as File);
        setUploadedImage(image.id, uploadedImage);
      } catch (error) {
        failedUploads += 1;
        const uploadError = error instanceof PlaceContributionApiError
          ? getFirstFieldError(error.fields?.image) ?? error.message
          : "بارگذاری این تصویر ناموفق بود.";
        if (!firstUploadError) firstUploadError = uploadError;
        if (error instanceof PlaceContributionApiError && error.code === "request_rate_limited") {
          setRetryAfterSeconds(error.retryAfterSeconds ?? 60);
        }
        setImageStatus(image.id, "error", uploadError);
      }
    }

    if (failedUploads > 0) {
      setImageUploadError(firstUploadError ?? "بارگذاری برخی تصاویر ناموفق بود. می‌توانید دوباره تلاش کنید.");
    }
    setIsUploadingImages(false);
    return failedUploads === 0;
  };

  const clientError = useMemo(() => {
    if (!fields.name.trim() || !fields.phone.trim() || !fields.city || !fields.category || !fields.address.trim() || !fields.description.trim()) {
      return "همه اطلاعات اصلی مکان را کامل کنید.";
    }

    if (!fields.googleMapsUrl.trim()) {
      return "لینک موقعیت کسب‌وکار در Google Maps را وارد کنید.";
    }

    if (!isHttpUrl(fields.googleMapsUrl.trim())) {
      return "لینک موقعیت باید یک آدرس معتبر Google Maps باشد.";
    }

    if (fields.websiteUrl.trim() && !isHttpUrl(fields.websiteUrl.trim())) {
      return "لینک وب‌سایت باید با http:// یا https:// آغاز شود.";
    }

    if (fields.websiteUrl.trim() && isGoogleMapsUrl(fields.websiteUrl.trim())) {
      return "در فیلد وب‌سایت، آدرس رسمی کسب‌وکار را وارد کنید؛ لینک Google Maps مربوط به این فیلد نیست.";
    }

    if (fields.instagramUrl.trim() && (!isHttpUrl(fields.instagramUrl.trim()) || !isInstagramUrl(fields.instagramUrl.trim()))) {
      return "لینک کامل صفحه رسمی Instagram را وارد کنید؛ مانند https://instagram.com/example";
    }

    if (!schedule.some((day) => day.enabled)) {
      return "حداقل یک روز و ساعت کاری آن را مشخص کنید.";
    }

    if (selectedTags.length === 0) {
      return "حداقل یک برچسب انتخاب کنید.";
    }

    if (selectedImages.length === 0) {
      return "حداقل یک تصویر از مکان انتخاب کنید.";
    }

    return null;
  }, [fields, schedule, selectedImages.length, selectedTags.length]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitLockRef.current || retryAfterSeconds > 0) return;
    setFormError(null);
    setFieldErrors({});
    setFieldCodes({});

    if (submissionsQuery.isLoading || submissionsQuery.isError) {
      setFormError("بررسی پیش‌نویس‌های قبلی انجام نشد. اتصال خود را بررسی و دوباره تلاش کنید.");
      return;
    }

    if (clientError) {
      setFormError(clientError);
      return;
    }

    submitLockRef.current = true;

    const openingSchedule = schedule
      .filter((day) => day.enabled)
      .map<OpeningScheduleDay>((day) => ({
        day_of_week: day.dayOfWeek,
        state: "intervals",
        intervals: [{
          opens_at: day.opensAt,
          closes_at: day.closesAt,
          crosses_midnight: day.closesAt <= day.opensAt,
        }],
      }));

    const payload: PlaceContributionDraft = {
      name: fields.name.trim(),
      city: Number(fields.city),
      categories: [Number(fields.category)],
      tags: selectedTags,
      address: fields.address.trim() || undefined,
      description: fields.description.trim() || undefined,
      phone: fields.phone.trim() || undefined,
      website_url: fields.websiteUrl.trim() || undefined,
      instagram_url: fields.instagramUrl.trim() || undefined,
      google_maps_url: fields.googleMapsUrl.trim() || undefined,
      opening_schedule: openingSchedule.length ? openingSchedule : undefined,
      timezone: "Asia/Tehran",
    };

    setIsSubmitting(true);
    try {
      let draft: PlaceContributionSubmission;
      if (editingSubmissionId) {
        const originalPayload = originalDraftPayloadRef.current;
        const changedPayload = originalPayload
          ? Object.fromEntries(Object.entries(payload).filter(([key, value]) => (
              JSON.stringify(value) !== JSON.stringify(originalPayload[key as keyof PlaceContributionDraft])
            ))) as Partial<PlaceContributionDraft>
          : payload;
        draft = Object.keys(changedPayload).length > 0
          ? await updatePlaceContributionDraft(editingSubmissionId, changedPayload)
          : { submission_id: editingSubmissionId, status: "draft" };
      } else {
        draft = await createPlaceContributionDraft(payload, idempotencyKey);
      }
      originalDraftPayloadRef.current = payload;
      setEditingSubmissionId(draft.submission_id);
      const imagesUploaded = await uploadSelectedImages(draft.submission_id);
      if (!imagesUploaded) {
        setIsRestoredDraft(true);
        setFormError("اطلاعات پیش‌نویس ذخیره شد، اما بارگذاری همه تصاویر کامل نشد. تصویر ناموفق را اصلاح و دوباره ارسال کنید.");
        return;
      }

      try {
        const finalizedSubmission = await submitPlaceContributionDraft(draft.submission_id);
        clearIdempotencyKey(user?.userId);
        queryClient.setQueryData<PlaceContributionSubmission[]>(
          ["place-contribution-submissions", user?.userId],
          (current = []) => current.filter((item) => item.submission_id !== draft.submission_id),
        );
        setEditingSubmissionId(null);
        setIsRestoredDraft(false);
        setSubmission(finalizedSubmission);
        toast.success("درخواست کسب‌وکار با موفقیت برای بررسی ارسال شد.", {
          className: "toast",
          toastId: "place-contribution-submitted",
        });
      } catch (error) {
        const message = error instanceof PlaceContributionApiError
          ? error.message
          : "ارسال درخواست برای بررسی ممکن نشد. لطفاً دوباره تلاش کنید.";
        setFormError(message);
        if (error instanceof PlaceContributionApiError) {
          if (error.code === "request_rate_limited") {
            setRetryAfterSeconds(error.retryAfterSeconds ?? 60);
          }
          const errors = error.fields ?? {};
          setFieldErrors(errors);
          setFieldCodes(error.fieldCodes ?? {});
          focusFirstInvalidField(errors);
        }
      }
    } catch (error) {
      if (error instanceof PlaceContributionApiError) {
        setFormError(error.message);
        if (error.code === "request_rate_limited") setRetryAfterSeconds(error.retryAfterSeconds ?? 60);
        if (error.code === "daily_contribution_limit") setDailyLimitReached(true);
        if (error.code === "active_draft_limit") {
          setShowNewForm(false);
          void submissionsQuery.refetch();
        }
        const errors = error.fields ?? {};
        setFieldErrors(errors);
        setFieldCodes(error.fieldCodes ?? {});
        focusFirstInvalidField(errors);
      } else {
        setFormError("ثبت پیش‌نویس ممکن نشد. لطفاً دوباره تلاش کنید.");
      }
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleFinalize = async () => {
    if (!submission) return;

    const hasPendingImageUpload = selectedImages.some((image) => image.status !== "uploaded");
    if (hasPendingImageUpload || !selectedImages.some((image) => image.status === "uploaded")) {
      setFinalizeError("پیش از ارسال برای بررسی، حداقل یک تصویر را با موفقیت بارگذاری کنید.");
      return;
    }

    setFinalizeError(null);
    setIsFinalizing(true);
    try {
      const result = await submitPlaceContributionDraft(submission.submission_id);
      clearIdempotencyKey(user?.userId);
      setSubmission(result);
      toast.success("درخواست کسب‌وکار با موفقیت برای بررسی ارسال شد.", {
        className: "toast",
        toastId: "place-contribution-submitted",
      });
    } catch (error) {
      if (error instanceof PlaceContributionApiError) {
        setFinalizeError(error.message);
      } else {
        setFinalizeError("ارسال درخواست برای بررسی ممکن نشد. لطفاً دوباره تلاش کنید.");
      }
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleCancelDraft = async (draft?: PlaceContributionSubmission) => {
    const targetDraft = draft ?? submission;
    if (!targetDraft || targetDraft.status !== "draft" || isCancellingDraft) return;
    if (!window.confirm("این پیش‌نویس لغو شود؟ اطلاعات و تصاویر ذخیره‌شده آن دیگر قابل ادامه نخواهد بود.")) return;

    setFinalizeError(null);
    setIsCancellingDraft(true);
    try {
      await deletePlaceContributionDraft(targetDraft.submission_id);
      queryClient.setQueryData<PlaceContributionSubmission[]>(
        ["place-contribution-submissions", user?.userId],
        (current = []) => current.filter((item) => item.submission_id !== targetDraft.submission_id),
      );
      if (editingSubmissionId === targetDraft.submission_id || submission?.submission_id === targetDraft.submission_id) {
        clearIdempotencyKey(user?.userId);
        setIdempotencyKey(getOrCreateIdempotencyKey(user?.userId));
        setFields(initialFields);
        setSelectedTags([]);
        setSchedule(initialSchedule);
        setSelectedImages([]);
        originalDraftPayloadRef.current = null;
        setEditingSubmissionId(null);
        setIsRestoredDraft(false);
        setSubmission(null);
      }
      void submissionsQuery.refetch();
      toast.success("پیش‌نویس با موفقیت لغو شد.", {
        className: "toast",
        toastId: "place-contribution-draft-cancelled",
      });
    } catch (error) {
      setFinalizeError(error instanceof PlaceContributionApiError
        ? error.message
        : "لغو پیش‌نویس ممکن نشد. لطفاً دوباره تلاش کنید.");
    } finally {
      setIsCancellingDraft(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col" dir="rtl">
      <Navbar />
      <main className="site-container flex-1 pb-10 pt-24 tablet:pb-10 tablet:pt-24">
        <div className="mx-auto max-w-3xl">
          <header className="mb-6 px-1 text-right tablet:px-0">
            <h1 className="text-xl text-[#222222] font-myYekanFaNumExtraBold tablet:text-2xl">کسب‌وکارتان را در لوتکا معرفی کنید.</h1>
            <p className="mt-2 max-w-2xl text-sm leading-7 text-gray-700 font-myYekanRegular tablet:text-sm">
              کافه، رستوران، اقامتگاه یا خدمات گردشگری دارید؟ اطلاعات کسب‌وکارتان را وارد کنید تا مسافران راحت‌تر شما را پیدا کنند.
            </p>
          </header>

          {!user ? (
            <section className="rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm">
              <h2 className="text-lg text-[#222222] font-myYekanFaNumDemiBold">برای ثبت مکان وارد حساب کاربری شوید</h2>
              <p className="mt-2 text-sm leading-7 text-gray-600 font-myYekanRegular">پس از ورود می‌توانید اطلاعات مکان را به‌صورت پیش‌نویس ثبت کنید.</p>
              <div className="mt-5 flex justify-center gap-3">
                <Link to="/login" className="inline-flex h-11 items-center justify-center rounded-xl border border-gray-200 bg-white px-5 text-sm text-[#222222] font-myYekanFaNumDemiBold">ورود</Link>
                <Link to="/signUp" className="inline-flex h-11 items-center justify-center rounded-xl bg-[#038545] px-5 text-sm text-white font-myYekanFaNumDemiBold">ثبت‌نام</Link>
              </div>
            </section>
          ) : submission ? (
            <section className="rounded-2xl border border-[#97d2b5] bg-white p-6 text-center shadow-sm">
              <CheckCircle2 className="mx-auto h-10 w-10 text-[#038545]" aria-hidden="true" />
              <h2 className="mt-3 text-lg text-[#222222] font-myYekanFaNumDemiBold">{submission.status === "pending" ? "درخواست معرفی کسب‌وکار شما دریافت شد" : "اطلاعات کسب‌وکار ذخیره شد"}</h2>
              <p className="mt-2 text-sm leading-7 text-gray-600 font-myYekanRegular">
                {submission.status === "pending" ? "پس از تایید اطلاعات در سایت لوتکا منتشر می‌شود." : "تصاویر را بارگذاری کنید و سپس درخواست را برای بررسی ارسال کنید."}
              </p>
              {isRestoredDraft && submission.status === "draft" && (
                <p className="mt-3 rounded-xl border border-[#97d2b5] bg-[#f3fefa] px-3 py-2 text-xs text-[#17643d] font-myYekanRegular">
                  پیش‌نویس قبلی شما بازیابی شد. می‌توانید آن را ادامه دهید یا لغو کنید و ثبت جدیدی انجام دهید.
                </p>
              )}
              {submission.warnings?.map((warning) => <p key={warning} className="mt-2 text-xs text-amber-700 font-myYekanRegular">{warning}</p>)}
              {submission.rejection_reason && <p className="mt-2 text-sm text-red-700 font-myYekanRegular">{submission.rejection_reason}</p>}
              {submission.status !== "pending" && (
                <div className="mt-5 text-right">
                  <ContributionImagePicker images={selectedImages} disabled={isUploadingImages || isFinalizing} onAdd={addImages} onRemove={removeImage} />
                  {imageUploadError && <p className="mt-3 text-center text-xs text-red-600 font-myYekanRegular">{imageUploadError}</p>}
                  {finalizeError && <p className="mt-3 text-center text-xs text-red-600 font-myYekanRegular">{finalizeError}</p>}
                  {selectedImages.some((image) => image.status === "ready" || image.status === "error") && (
                    <button
                      type="button"
                      onClick={() => void uploadSelectedImages(submission.submission_id)}
                      disabled={isUploadingImages || isFinalizing}
                      className="mx-auto mt-4 flex h-10 items-center justify-center rounded-xl bg-[#038545] px-5 text-sm text-white transition-colors hover:bg-[#068646] disabled:cursor-not-allowed disabled:opacity-50 font-myYekanFaNumDemiBold"
                    >
                      {isUploadingImages ? "در حال بارگذاری..." : "بارگذاری تصاویر انتخاب‌شده"}
                    </button>
                  )}
                  {selectedImages.length > 0 && selectedImages.every((image) => image.status === "uploaded") && (
                    <button
                      type="button"
                      onClick={() => void handleFinalize()}
                      disabled={isFinalizing || isUploadingImages}
                      className="mx-auto mt-3 flex h-11 items-center justify-center rounded-xl bg-[#038545] px-5 text-sm text-white transition-colors hover:bg-[#068646] disabled:cursor-not-allowed disabled:opacity-50 font-myYekanFaNumDemiBold"
                    >
                      {isFinalizing ? "در حال ارسال..." : "ارسال برای بررسی"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleCancelDraft()}
                    disabled={isCancellingDraft || isUploadingImages || isFinalizing}
                    className="mx-auto mt-3 flex h-10 items-center justify-center rounded-xl border border-red-200 bg-white px-4 text-xs text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 font-myYekanFaNumDemiBold"
                  >
                    {isCancellingDraft ? "در حال لغو..." : "لغو این پیش‌نویس و ثبت جدید"}
                  </button>
                </div>
              )}
            </section>
          ) : !showNewForm && !editingSubmissionId && Boolean(submissionsQuery.data?.length) ? (
            <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm tablet:p-7">
              <div className="flex flex-wrap items-start justify-between gap-3 text-right">
                <div>
                  <h2 className="text-lg text-[#222222] font-myYekanFaNumDemiBold">پیش‌نویس‌های ثبت کسب‌وکار</h2>
                  <p className="mt-1 text-xs leading-6 text-gray-600 font-myYekanRegular">پیش‌نویس ناقص را تکمیل یا ویرایش کنید؛ سپس برای بررسی ارسال کنید.</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    clearIdempotencyKey(user?.userId);
                    setIdempotencyKey(getOrCreateIdempotencyKey(user?.userId));
                    setFields(initialFields);
                    setSelectedTags([]);
                    setSchedule(initialSchedule);
                    setSelectedImages([]);
                    originalDraftPayloadRef.current = null;
                    setShowNewForm(true);
                  }}
                  className="h-10 rounded-xl border border-gray-200 bg-white px-4 text-xs text-[#222222] transition-colors hover:bg-gray-50 font-myYekanFaNumDemiBold"
                >
                  ثبت کسب‌وکار جدید
                </button>
              </div>

              <div className="mt-5 grid gap-3">
                {submissionsQuery.data?.map((draft) => (
                  <article key={draft.submission_id} className="rounded-xl border border-gray-200 p-4 text-right">
                    <div className="flex flex-col gap-4 tablet:flex-row tablet:items-center tablet:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-sm text-[#222222] font-myYekanFaNumDemiBold">{draft.name || "پیش‌نویس کسب‌وکار"}</h3>
                          <span className={`rounded-lg px-2 py-1 text-[10px] font-myYekanFaNumMedium ${
                            draft.validation_state === "valid"
                              ? "bg-[#f3fefa] text-[#038545]"
                              : "bg-amber-50 text-amber-700"
                          }`}>
                            {draft.validation_state === "valid" ? "آماده تکمیل" : "نیازمند اصلاح"}
                          </span>
                        </div>
                        <p className="mt-2 text-xs text-gray-600 font-myYekanFaNumRegular">آخرین تغییر: {formatDraftDate(draft.updated_at ?? draft.created_at)}</p>
                        {draft.warnings?.slice(0, 2).map((warning) => (
                          <p key={warning} className="mt-1 text-xs text-amber-700 font-myYekanRegular">{warning}</p>
                        ))}
                      </div>

                      {draft.images?.length ? (
                        <div className="flex shrink-0 gap-2" dir="ltr">
                          {draft.images.slice(0, 3).map((image) => (
                            <img key={image.id} src={image.url} alt="تصویر پیش‌نویس کسب‌وکار" className="h-14 w-14 rounded-lg border border-gray-200 object-cover" />
                          ))}
                        </div>
                      ) : null}

                      <div className="flex shrink-0 gap-2">
                        <button
                          type="button"
                          onClick={() => void handleOpenDraft(draft)}
                          disabled={isLoadingDraft || isCancellingDraft}
                          className="h-10 rounded-xl bg-[#038545] px-4 text-xs text-white transition-colors hover:bg-[#068646] disabled:cursor-not-allowed disabled:opacity-50 font-myYekanFaNumDemiBold"
                        >
                          {isLoadingDraft ? "در حال بازکردن..." : "تکمیل یا ویرایش"}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleCancelDraft(draft)}
                          disabled={isLoadingDraft || isCancellingDraft}
                          className="h-10 rounded-xl border border-red-200 bg-white px-3 text-xs text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 font-myYekanFaNumDemiBold"
                        >
                          لغو
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ) : (
            <form ref={formRef} onSubmit={handleSubmit} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm tablet:p-7">
              {editingSubmissionId && (
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#97d2b5] bg-[#f3fefa] px-4 py-3">
                  <div className="text-right">
                    <p className="text-sm text-[#17643d] font-myYekanFaNumDemiBold">در حال تکمیل یا ویرایش پیش‌نویس</p>
                    <p className="mt-1 text-xs text-gray-600 font-myYekanRegular">اطلاعات قبلی را اصلاح کنید و سپس درخواست را برای بررسی بفرستید.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingSubmissionId(null);
                      setIsRestoredDraft(false);
                      setSelectedImages([]);
                      originalDraftPayloadRef.current = null;
                    }}
                    className="text-xs text-[#17643d] underline-offset-4 hover:underline font-myYekanFaNumDemiBold"
                  >
                    بازگشت به پیش‌نویس‌ها
                  </button>
                </div>
              )}
              {formError && <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 font-myYekanRegular">{formError}</div>}
              {submissionsQuery.isError && (
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 font-myYekanRegular">
                  <span>{submissionsQuery.error instanceof Error ? submissionsQuery.error.message : "بررسی پیش‌نویس‌های قبلی ممکن نشد."}</span>
                  <button
                    type="button"
                    onClick={() => void submissionsQuery.refetch()}
                    disabled={submissionsQuery.isFetching || retryAfterSeconds > 0}
                    className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs text-amber-900 disabled:cursor-not-allowed disabled:opacity-50 font-myYekanFaNumDemiBold"
                  >
                    {retryAfterSeconds > 0 ? `${new Intl.NumberFormat("fa-IR").format(retryAfterSeconds)} ثانیه` : "بررسی دوباره"}
                  </button>
                </div>
              )}
              {taxonomyError && <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 font-myYekanRegular">دریافت شهرها، دسته‌بندی‌ها یا برچسب‌ها ممکن نشد. صفحه را دوباره بارگذاری کنید.</div>}

              <div className="grid gap-4 tablet:grid-cols-2">
                <Field field="name" label="نام کسب‌وکار" required error={fieldMessages("name")}>
                  <input value={fields.name} onChange={(event) => updateField("name", event.target.value)} className={inputClassName} placeholder="مثلاً: کافه نارنج" />
                </Field>
                <Field field="phone" label="شماره تماس" required error={fieldMessages("phone")}>
                  <input value={fields.phone} inputMode="tel" onChange={(event) => updateField("phone", event.target.value)} className={inputClassName} />
                </Field>
                <Field field="website_url" label="وب‌سایت" error={fieldMessages("website_url")}>
                  <input type="url" dir="ltr" value={fields.websiteUrl} onChange={(event) => updateField("websiteUrl", event.target.value, "website_url")} className={inputClassName} placeholder="https://example.com" />
                </Field>
                <Field field="instagram_url" label="لینک اینستاگرام" error={fieldMessages("instagram_url")}>
                  <input type="url" dir="ltr" value={fields.instagramUrl} onChange={(event) => updateField("instagramUrl", event.target.value, "instagram_url")} className={inputClassName} placeholder="https://instagram.com/example" />
                </Field>
                <Field field="city" label="شهر" required error={fieldMessages("city")}>
                  <select value={fields.city} disabled={isTaxonomyLoading || !isTaxonomyReady} onChange={(event) => updateField("city", event.target.value)} className={inputClassName}>
                    <option value="">{isTaxonomyLoading ? "در حال دریافت شهرها..." : "انتخاب شهر"}</option>
                    {citiesQuery.data?.map((city) => <option key={city.id} value={city.id}>{city.name}</option>)}
                  </select>
                </Field>
                <Field field="categories" label="نوع کسب‌وکار" required error={fieldMessages("categories")}>
                  <select value={fields.category} disabled={isTaxonomyLoading || !isTaxonomyReady} onChange={(event) => updateField("category", event.target.value, "categories")} className={inputClassName}>
                    <option value="">{isTaxonomyLoading ? "در حال دریافت دسته‌بندی‌ها..." : "انتخاب نوع کسب‌وکار"}</option>
                    {categoriesQuery.data?.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                  </select>
                </Field>
              </div>

              <section data-field="tags" tabIndex={-1} className="mt-4 rounded-xl border border-gray-200 p-4 outline-none focus:ring-2 focus:ring-[#038545]/20" aria-labelledby="contribution-tags-title">
                <div className="flex items-center gap-1 text-sm text-[#222222] font-myYekanFaNumDemiBold">
                  <h2 id="contribution-tags-title">برچسب‌ها</h2><RequiredMark />
                </div>
                <p className="mt-1 text-xs leading-6 text-gray-600 font-myYekanRegular">حداقل یک برچسب مرتبط با مکان انتخاب کنید.</p>
                {tagsQuery.data && tagsQuery.data.length > 0 ? (
                  <>
                    <div className={`mt-3 ${showAllTags ? "max-h-52 overflow-y-auto pe-1" : ""}`}>
                      <div className="flex flex-wrap gap-2">
                        {(showAllTags ? tagsQuery.data : tagsQuery.data.slice(0, 5)).map((tag) => {
                          const isSelected = selectedTags.includes(tag.id);
                          return (
                            <button
                              key={tag.id}
                              type="button"
                              aria-pressed={isSelected}
                              onClick={() => toggleTag(tag.id)}
                              className={`min-h-9 rounded-xl border px-3 py-1.5 text-xs transition-colors font-myYekanFaNumMedium ${
                                isSelected
                                  ? "border-[#038545] bg-[#f3fefa] text-[#038545]"
                                  : "border-gray-200 bg-white text-gray-700 hover:border-[#97d2b5]"
                              }`}
                            >
                              {tag.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3">
                      {selectedTags.length > 0 ? <span className="text-xs text-[#038545] font-myYekanFaNumMedium">{selectedTags.length} برچسب انتخاب شده</span> : <span />}
                      {tagsQuery.data.length > 5 && (
                        <button
                          type="button"
                          aria-expanded={showAllTags}
                          onClick={() => setShowAllTags((current) => !current)}
                          className="text-xs text-[#038545] underline-offset-4 transition-colors hover:text-[#068646] hover:underline font-myYekanFaNumDemiBold"
                        >
                          {showAllTags ? "نمایش کمتر" : "بیشتر"}
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="mt-3 text-xs text-gray-500 font-myYekanRegular">برچسبی برای انتخاب موجود نیست.</p>
                )}
                {fieldMessages("tags").map((message) => <p key={message} className="mt-2 text-[11px] text-red-600 font-myYekanRegular">{message}</p>)}
              </section>

              <div className="mt-4">
                <Field field="address" label="آدرس" required error={fieldMessages("address")}>
                  <input value={fields.address} onChange={(event) => updateField("address", event.target.value)} className={inputClassName} />
                </Field>
              </div>
              <div className="mt-4">
                <Field field="description" label="معرفی کسب‌وکار" required error={fieldMessages("description")}>
                  <textarea value={fields.description} onChange={(event) => updateField("description", event.target.value)} rows={4} className={`${inputClassName} h-auto resize-y py-3`} placeholder="درباره خدمات، فضا و ویژگی‌های خاص کسب‌وکارتان بنویسید." />
                </Field>
              </div>

              <div className="mt-5 rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-1 text-sm text-[#222222] font-myYekanFaNumDemiBold">موقعیت<RequiredMark /></div>
                <p className="mt-1 text-xs leading-6 text-gray-600 font-myYekanRegular">لینک موقعیت کسب‌وکار را از Google Maps وارد کنید.</p>
                <div data-field="google_maps_url" className="mt-3 scroll-mt-24">
                  <input aria-label="لینک موقعیت کسب‌وکار در Google Maps" type="url" dir="ltr" value={fields.googleMapsUrl} onChange={(event) => updateField("googleMapsUrl", event.target.value, "google_maps_url")} className={inputClassName} placeholder="https://maps.app.goo.gl/..." />
                  {fieldMessages("google_maps_url").map((message) => <span key={message} className="mt-1 block text-[11px] text-red-600 font-myYekanRegular">{message}</span>)}
                  {(fieldCodes.google_maps_url?.includes("place_pin_not_found") || fieldCodes.location?.includes("invalid_location")) && <p className="mt-2 text-xs leading-6 text-amber-700 font-myYekanRegular">لینک Share پین دقیق را وارد کنید؛ لینک نمای جست‌وجوی نقشه قابل قبول نیست.</p>}
                </div>
              </div>

              <div className="mt-5 rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-1 text-sm text-[#222222] font-myYekanFaNumDemiBold"><Clock3 className="h-4 w-4 text-[#038545]" /> ساعت کاری<RequiredMark /></div>
                <p className="mt-1 text-xs leading-6 text-gray-600 font-myYekanRegular">حداقل یک روز و ساعت کاری آن را مشخص کنید.</p>
                <div className="mt-3 rounded-lg border border-[#97d2b5]/60 bg-[#f3fefa] p-3">
                  <label className="flex cursor-pointer items-center justify-between gap-3 text-sm text-[#222222] font-myYekanFaNumDemiBold">
                    <span>همه‌روزه</span>
                    <input type="checkbox" checked={isEveryDay} onChange={(event) => toggleEveryDay(event.target.checked)} className="h-4 w-4 accent-[#038545]" />
                  </label>
                  {isEveryDay && (
                    <div className="mt-3 grid grid-cols-2 gap-2" dir="ltr">
                      <input aria-label="ساعت شروع همه‌روزه" type="time" value={everyDayHours.opensAt} onChange={(event) => updateEveryDayHours("opensAt", event.target.value)} className={`${inputClassName} text-center`} />
                      <input aria-label="ساعت پایان همه‌روزه" type="time" value={everyDayHours.closesAt} onChange={(event) => updateEveryDayHours("closesAt", event.target.value)} className={`${inputClassName} text-center`} />
                    </div>
                  )}
                </div>
                {!isEveryDay && <div className="mt-3 grid gap-2 tablet:grid-cols-2">
                  {schedule.map((day) => (
                    <div key={day.dayOfWeek} className="rounded-lg border border-gray-200 bg-white p-3">
                      <label className="flex cursor-pointer items-center justify-between gap-3 text-sm text-[#222222] font-myYekanFaNumMedium">
                        <span>{day.label}</span>
                        <input type="checkbox" checked={day.enabled} onChange={(event) => updateScheduleDay(day.dayOfWeek, { enabled: event.target.checked })} className="h-4 w-4 accent-[#038545]" />
                      </label>
                      {day.enabled && (
                        <div className="mt-3 grid grid-cols-2 gap-2" dir="ltr">
                          <input aria-label={`ساعت شروع ${day.label}`} type="time" value={day.opensAt} onChange={(event) => updateScheduleDay(day.dayOfWeek, { opensAt: event.target.value })} className={`${inputClassName} text-center`} />
                          <input aria-label={`ساعت پایان ${day.label}`} type="time" value={day.closesAt} onChange={(event) => updateScheduleDay(day.dayOfWeek, { closesAt: event.target.value })} className={`${inputClassName} text-center`} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>}
              </div>

              <div className="mt-5">
                <ContributionImagePicker required images={selectedImages} disabled={isSubmitting} onAdd={addImages} onRemove={removeImage} />
                {imageUploadError && <p className="mt-2 text-xs text-red-600 font-myYekanRegular">{imageUploadError}</p>}
              </div>

              <div className="mt-6 flex flex-col-reverse gap-3 tablet:flex-row tablet:items-center tablet:justify-between">
                <p className="text-xs leading-6 text-gray-500 font-myYekanRegular"></p>
                <button type="submit" disabled={isSubmitting || submissionsQuery.isLoading || retryAfterSeconds > 0 || (!editingSubmissionId && dailyLimitReached) || !isTaxonomyReady} className="inline-flex h-11 items-center justify-center rounded-xl bg-[#038545] px-5 text-sm text-white transition-colors hover:bg-[#068646] disabled:cursor-not-allowed disabled:opacity-50 font-myYekanFaNumDemiBold">
                  {isSubmitting
                    ? "در حال ثبت..."
                    : submissionsQuery.isLoading
                      ? "در حال بررسی پیش‌نویس‌ها..."
                    : retryAfterSeconds > 0
                      ? `تلاش مجدد پس از ${new Intl.NumberFormat("fa-IR").format(retryAfterSeconds)} ثانیه`
                      : !editingSubmissionId && dailyLimitReached
                        ? "سقف ثبت روزانه تکمیل شده است"
                      : editingSubmissionId
                        ? "ذخیره اصلاحات و ارسال برای بررسی"
                        : "ثبت کسب‌وکار شما"}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

const inputClassName = "h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-[#222222] outline-none transition placeholder:text-gray-400 focus:border-[#038545] focus:ring-2 focus:ring-[#038545]/15 disabled:cursor-not-allowed disabled:bg-gray-100 font-myYekanFaNumRegular";

const RequiredMark = () => <span className="mr-1 text-red-600" aria-label="اجباری">*</span>;

const Field: React.FC<{ field: string; label: string; required?: boolean; error?: string[]; children: React.ReactNode }> = ({ field, label, required = false, error = [], children }) => (
  <label data-field={field} className="block scroll-mt-24 text-right">
    <span className="mb-1.5 block text-xs text-[#222222] font-myYekanFaNumMedium">{label}{required && <RequiredMark />}</span>
    {children}
    {error.map((message) => <span key={message} className="mt-1 block text-[11px] text-red-600 font-myYekanRegular">{message}</span>)}
  </label>
);

const ContributionImagePicker: React.FC<{
  images: SelectedImage[];
  required?: boolean;
  disabled?: boolean;
  onAdd: (files: FileList | null) => void;
  onRemove: (imageId: string) => void;
}> = ({ images, required = false, disabled = false, onAdd, onRemove }) => (
  <section className="rounded-xl border border-gray-200 p-4 text-right">
    <div className="flex items-start justify-between gap-3">
      <div>
        <h2 className="text-sm text-[#222222] font-myYekanFaNumDemiBold">عکس‌های کسب‌وکارتان{required && <RequiredMark />}</h2>
        <p className="mt-1 text-xs leading-6 text-gray-600 font-myYekanRegular">عکس‌های واضح از ورودی، فضای داخلی و خدمات اضافه کنید. حداکثر پنج تصویر{required ? " و حداقل یک تصویر الزامی است." : "."}</p>
      </div>
      <label
        htmlFor="place-contribution-images"
        className={`inline-flex h-10 shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-[#038545]/25 bg-white px-3 text-xs text-[#038545] transition-colors hover:bg-[#f3fefa] font-myYekanFaNumDemiBold ${disabled || images.length >= 5 ? "pointer-events-none opacity-50" : ""}`}
      >
        <Upload className="h-4 w-4" aria-hidden="true" />
        افزودن تصویر
        <input
          id="place-contribution-images"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          disabled={disabled || images.length >= 5}
          className="sr-only"
          onChange={(event) => {
            onAdd(event.target.files);
            event.currentTarget.value = "";
          }}
        />
      </label>
    </div>

    {images.length > 0 && (
      <div className="mt-4 grid grid-cols-3 gap-2 tablet:grid-cols-5">
        {images.map((image) => (
          <div key={image.id} className="group relative aspect-square overflow-hidden rounded-lg border border-gray-200 bg-white">
            <img src={image.previewUrl} alt="پیش‌نمایش تصویر مکان" className="h-full w-full object-cover" />
            {image.status !== "uploaded" && (
              <button
                type="button"
                aria-label="حذف تصویر"
                disabled={disabled || image.status === "uploading"}
                onClick={() => onRemove(image.id)}
                className="absolute left-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/65 text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
            <span className={`absolute inset-x-0 bottom-0 px-1.5 py-1 text-center text-[10px] text-white font-myYekanFaNumMedium ${
              image.status === "uploaded" ? "bg-[#038545]/90" : image.status === "error" ? "bg-red-600/90" : "bg-black/60"
            }`}>
              {image.status === "uploaded" ? "بارگذاری شد" : image.status === "uploading" ? "در حال بارگذاری" : image.status === "error" ? "ناموفق" : "آماده بارگذاری"}
            </span>
          </div>
        ))}
      </div>
    )}
  </section>
);

export default PlaceContributionPage;
